import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { countsBySubmitterSchema, type CountsBySubmitter } from '@/types/countsBySubmitter.types';
import {
  paginatedTeachersSchema,
  teacherDesignationListSchema,
  teacherDetailSchema,
  teacherSummarySchema,
  type PaginatedTeachers,
  type TeacherCountsBySubmitterParams,
  type TeacherDetail,
  type TeacherLookupItem,
  type TeacherListParams,
  type TeacherSummary,
  type TeacherSummaryParams,
} from '../types/teacherSubmission.types';

export const teacherSubmissionQueryKeys = {
  all: () => ['teacher-submissions'] as const,
  lists: () => [...teacherSubmissionQueryKeys.all(), 'list'] as const,
  list: (params: TeacherListParams) => [...teacherSubmissionQueryKeys.lists(), params] as const,
  summaries: () => [...teacherSubmissionQueryKeys.all(), 'summary'] as const,
  summary: (params: TeacherSummaryParams) =>
    [...teacherSubmissionQueryKeys.summaries(), params] as const,
  countsBySubmitter: (params: TeacherCountsBySubmitterParams) =>
    [...teacherSubmissionQueryKeys.all(), 'counts-by-submitter', params] as const,
  detail: (id: number) => [...teacherSubmissionQueryKeys.all(), 'detail', id] as const,
  designations: () => [...teacherSubmissionQueryKeys.all(), 'designations'] as const,
};

export const teacherListInfiniteQueryOptions = (params: TeacherListParams) =>
  infiniteQueryOptions({
    queryKey: teacherSubmissionQueryKeys.list(params),
    queryFn: async ({ pageParam }): Promise<PaginatedTeachers> => {
      const res = await apiClient.get(API.teacher.list, {
        params: { ...params, page: pageParam },
      });
      return paginatedTeachersSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
  });

export const teacherSummaryQueryOptions = (params: TeacherSummaryParams) =>
  queryOptions({
    queryKey: teacherSubmissionQueryKeys.summary(params),
    queryFn: async (): Promise<TeacherSummary> => {
      const res = await apiClient.get(API.teacher.summary, { params });
      return teacherSummarySchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const teacherCountsBySubmitterQueryOptions = (params: TeacherCountsBySubmitterParams) =>
  queryOptions({
    queryKey: teacherSubmissionQueryKeys.countsBySubmitter(params),
    queryFn: async (): Promise<CountsBySubmitter> => {
      const res = await apiClient.get(API.teacher.countsBySubmitter, { params });
      return countsBySubmitterSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const teacherDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: teacherSubmissionQueryKeys.detail(id),
    queryFn: async (): Promise<TeacherDetail> => {
      const res = await apiClient.get(API.teacher.detail(id));
      return teacherDetailSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const activeTeacherDesignationsQueryOptions = () =>
  queryOptions({
    queryKey: teacherSubmissionQueryKeys.designations(),
    queryFn: async (): Promise<TeacherLookupItem[]> => {
      const res = await apiClient.get(API.teacher.activeDesignations);
      return teacherDesignationListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });
